import { useEffect, useRef, useState } from "react";
import { useDevice } from "get-life-storybook-ts/lib/components/Hooks";
import { useTracker } from "../../contexts/TrackingContext";
export interface IndependentCalculatorPropsI {
  backgroundColor?: string;
}


interface FormDataI {
  birthDate: string;
  email: string;
  phone: string;
  postalCode: string;
  locality: string;
  isSelfEmployed: string;
  profession: string;
  dailyCompensation: string;
  hasSurgeryPlanned: string;
  hasCurrentTreatment: string;
  hasChronicDisease: string;
}

const errorMessages: Record<string, string> = {
  birthDate: "Por favor introduce una fecha de nacimiento válida",
  email: "Por favor inserte un correo electrónico correcto",
  phone: "Por favor inserte un número de teléfono correcto",
  postalCode: "Por favor introduce un código postal o dirección",
  locality: "Por favor introduce una localidad",
  isSelfEmployed: "Por favor seleccione una opción",
  profession: "Por favor introduce tu profesión",
  dailyCompensation: "Por favor seleccione una indemnización diaria",
  hasSurgeryPlanned: "Por favor seleccione una opción",
  hasCurrentTreatment: "Por favor seleccione una opción",
  hasChronicDisease: "Por favor seleccione una opción",
};

const IndependentCalculatorController = () => {
  const API_URL = process.env.GATSBY_API_URL;

  const [formData, setFormData] = useState<FormDataI>({
    birthDate: "",
    email: "",
    phone: "",
    postalCode: "",
    locality: "",
    isSelfEmployed: "",
    profession: "",
    dailyCompensation: "",
    hasSurgeryPlanned: "",
    hasCurrentTreatment: "",
    hasChronicDisease: "",
  });

  const [formError, setFormError] = useState<any>({});
  const [actualStep, setActualStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { isMobile } = useDevice();
  const { track } = useTracker();
  const stepStartTime = useRef<number>(Date.now());
  const focusedFields = useRef<Set<string>>(new Set());

  const optionsYesNo = [
    { label: "Sí", value: "yes" },
    { label: "No", value: "no" },
  ];

  const optionsDailyCompensation = [
    { label: "30 €/día", value: "30" },
    { label: "60 €/día", value: "60" },
    { label: "90 €/día", value: "90" },
    { label: "120 €/día", value: "120" },
  ];

  const validateEmail = async (email: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/public/email/validator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const fieldValidation = async (
    fieldData: Record<string, any>,
    key: string,
    currentErrors: any
  ) => {
    const value = fieldData[key];
    if (!value) {
      return {
        ...currentErrors,
        [key]: { error: true, message: errorMessages[key] },
      };
    }

    if (key === "email") {
      const valid = await validateEmail(value);
      return {
        ...currentErrors,
        [key]: valid
          ? { error: false, message: "" }
          : { error: true, message: errorMessages[key] },
      };
    }

    if (key === "phone") {
      const phoneRegex = /^[\d\s\-\(\)\+]+$/;
      return {
        ...currentErrors,
        [key]: phoneRegex.test(value)
          ? { error: false, message: "" }
          : { error: true, message: errorMessages[key] },
      };
    }

    return { ...currentErrors, [key]: { error: false, message: "" } };
  };

  const validateFields = async (fields: string[]) => {
    let errors: any = {};
    for (const key of fields) {
      errors = await fieldValidation(formData, key, errors);
    }
    const hasErrors = Object.values(errors).some((e: any) => e.error);
    return hasErrors ? errors : undefined;
  };

  const handleChange = async (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formError?.[field]?.error) {
      const updated = await fieldValidation(
        { ...formData, [field]: value },
        field,
        {}
      );
      setFormError((prev: any) => ({ ...prev, ...updated }));
    }
  };

  const handleBlur = async (field: string) => {
    if (formData[field as keyof FormDataI]) {
      const updated = await fieldValidation(formData, field, {});
      setFormError((prev: any) => ({ ...prev, ...updated }));
      if (updated[field]?.error) {
        track("validation_error", { field, error_type: field === "email" ? "api" : "format", step: actualStep, calculator: "independent" });
      } else {
        track("field_completed", { field, step: actualStep, calculator: "independent" });
      }
    }
  };

  const handleFocus = (field: string) => {
    if (!focusedFields.current.has(field)) {
      focusedFields.current.add(field);
      track("field_focused", { field, step: actualStep, calculator: "independent" });
    }
  };

  const step1Fields = [
    "birthDate",
    "email",
    "phone",
    "postalCode",
    "locality",
  ];

  const step2Fields = [
    "isSelfEmployed",
    "profession",
    "dailyCompensation",
  ];

  const step3Fields = [
    "hasSurgeryPlanned",
    "hasCurrentTreatment",
    "hasChronicDisease",
  ];

  const handleButton = async () => {
    const currentFields = [step1Fields, step2Fields, step3Fields][actualStep - 1];
    const errors = await validateFields(currentFields);
    if (errors) {
      setFormError(errors);
      return;
    }

    setFormError({});

    if (actualStep < 3) {
      track("step_completed", { step: actualStep, calculator: "independent", time_on_step_ms: Date.now() - stepStartTime.current });
      stepStartTime.current = Date.now();
      focusedFields.current.clear();
      setActualStep(actualStep + 1);
    } else {
      await submitForm();
    }
  };

  const submitForm = async () => {
    setLoading(true);
    try {
      const payload = {
        birthDate: formData.birthDate,
        email: formData.email,
        phone: formData.phone,
        postalCode: formData.postalCode,
        locality: formData.locality,
        isSelfEmployed: formData.isSelfEmployed === "yes",
        profession: formData.profession,
        dailyCompensation: formData.dailyCompensation,
        hasSurgeryPlanned: formData.hasSurgeryPlanned === "yes",
        hasCurrentTreatment: formData.hasCurrentTreatment === "yes",
        hasChronicDisease: formData.hasChronicDisease === "yes",
      };

      await fetch(`${API_URL}/public/independent-worker/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      track("lead_created", { email: formData.email, source: "independent-calculator" });
      track("form_submitted", { calculator: "independent", email: formData.email });

      setSubmitted(true);
    } catch (error) {
      track("submission_error", { error_type: "network", calculator: "independent" });
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    track("calculator_viewed", { calculator: "independent" });
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const payload = {
        last_step: actualStep,
        calculator: "independent",
        time_on_step_ms: Date.now() - stepStartTime.current,
      };
      if (document.visibilityState === "hidden") {
        track("session_pause", payload, true);
      } else {
        track("session_resume", { last_step: actualStep, calculator: "independent" });
      }
    };

    const handlePageHide = () => {
      track("session_end", {
        last_step: actualStep,
        calculator: "independent",
        time_on_step_ms: Date.now() - stepStartTime.current,
      }, true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [actualStep]);

  const trackStepBack = (from: number, to: number) => {
    track("step_back", { from, to, calculator: "independent" });
    stepStartTime.current = Date.now();
    focusedFields.current.clear();
  };

  return {
    actualStep,
    formData,
    formError,
    handleBlur,
    handleButton,
    handleChange,
    handleFocus,
    isMobile,
    loading,
    optionsDailyCompensation,
    optionsYesNo,
    setActualStep,
    submitted,
    trackStepBack,
  };
};

export default IndependentCalculatorController;
